/* Generate a post-import migration report PDF.
 * Lists migrated employees and highlights those who still need a password reset.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = { r: 27, g: 91, b: 191 };          // #376DB3
const WARN = { r: 217, g: 119, b: 6 };           // #B45309
const OK = { r: 5, g: 150, b: 105 };             // #059669
const MUTED = { r: 100, g: 116, b: 139 };        // #64748B

const fmtDate = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function buildMigrationReportPdf(result) {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;

    // Header band
    doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
    doc.rect(0, 0, pageW, 26, "F");
    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("Splendor Sud S.r.l. — Report Migrazione", margin, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Generato il ${fmtDate()}`, margin, 19);

    // Counters card
    doc.setTextColor(15);
    doc.setFontSize(11);
    let y = 36;
    const stats = [
        { label: "Creati", value: result.created || 0, color: OK },
        { label: "Aggiornati", value: result.updated || 0, color: BRAND },
        { label: "Con password", value: result.password_imported || 0, color: OK },
        { label: "Reset richiesto", value: result.needs_reset || 0, color: WARN },
        { label: "Saltati", value: result.skipped || 0, color: MUTED },
    ];
    const colW = (pageW - margin * 2) / stats.length;
    stats.forEach((s, i) => {
        const x = margin + colW * i;
        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x + 1, y, colW - 2, 18, 1.5, 1.5, "FD");
        doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.text(s.label.toUpperCase(), x + 4, y + 5);
        doc.setTextColor(s.color.r, s.color.g, s.color.b);
        doc.setFontSize(16);
        doc.text(String(s.value), x + 4, y + 14);
    });
    y += 24;

    // Section: dipendenti che necessitano reset
    const migrated = Array.isArray(result.migrated) ? result.migrated : [];
    const needReset = migrated.filter((m) => !m.has_password);
    const withPwd = migrated.filter((m) => m.has_password);

    if (needReset.length > 0) {
        doc.setTextColor(WARN.r, WARN.g, WARN.b);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(`Da contattare per nuova password (${needReset.length})`, margin, y);
        doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(
            "Questi dipendenti non avevano una password compatibile nel vecchio portale. Invia loro le nuove credenziali dal portale.",
            margin,
            y + 4
        );
        y += 8;
        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["#", "Matricola", "Cognome", "Nome", "Email", "Stato"]],
            body: needReset.map((m, i) => [
                String(i + 1),
                m.matricola || "—",
                m.cognome || "—",
                m.nome || "—",
                m.email || "—",
                m.action === "created" ? "Nuovo" : "Aggiornato",
            ]),
            theme: "striped",
            headStyles: { fillColor: [WARN.r, WARN.g, WARN.b], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8.5, textColor: 30 },
            alternateRowStyles: { fillColor: [254, 243, 199] },
            styles: { cellPadding: 1.6 },
            columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 22 },
                2: { cellWidth: 32 },
                3: { cellWidth: 30 },
                5: { cellWidth: 22 },
            },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    if (withPwd.length > 0) {
        if (y > 260) { doc.addPage(); y = 18; }
        doc.setTextColor(OK.r, OK.g, OK.b);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(`Password importata correttamente (${withPwd.length})`, margin, y);
        doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(
            "Possono accedere al nuovo portale con la stessa password che usavano prima.",
            margin,
            y + 4
        );
        y += 8;
        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["#", "Matricola", "Cognome", "Nome", "Email", "Stato"]],
            body: withPwd.map((m, i) => [
                String(i + 1),
                m.matricola || "—",
                m.cognome || "—",
                m.nome || "—",
                m.email || "—",
                m.action === "created" ? "Nuovo" : "Aggiornato",
            ]),
            theme: "striped",
            headStyles: { fillColor: [OK.r, OK.g, OK.b], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8.5, textColor: 30 },
            alternateRowStyles: { fillColor: [236, 253, 245] },
            styles: { cellPadding: 1.6 },
            columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 22 },
                2: { cellWidth: 32 },
                3: { cellWidth: 30 },
                5: { cellWidth: 22 },
            },
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    if (result.errors?.length > 0) {
        if (y > 260) { doc.addPage(); y = 18; }
        doc.setTextColor(WARN.r, WARN.g, WARN.b);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(`Errori (${result.errors.length})`, margin, y);
        y += 4;
        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["Riga", "Errore"]],
            body: result.errors.slice(0, 80).map((e) => [
                String(e.index ?? "—"),
                String(e.error || ""),
            ]),
            theme: "grid",
            headStyles: { fillColor: [WARN.r, WARN.g, WARN.b], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8, textColor: 30 },
            styles: { cellPadding: 1.4 },
            columnStyles: { 0: { cellWidth: 18 } },
        });
    }

    // Footer page numbers
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
        doc.setFontSize(8);
        doc.text(`Pagina ${i}/${pages}`, pageW - margin, 290, { align: "right" });
        doc.text("Splendor Sud · Portale HR", margin, 290);
    }

    const ts = new Date().toISOString().slice(0, 10);
    doc.save(`splendorsud-report-migrazione-${ts}.pdf`);
}
